"use client";

import { useEffect } from "react";
import { AlertTriangle, RefreshCw, Home } from "lucide-react";
import { Header } from "@/components/layout/header";
import { Footer } from "@/components/layout/footer";
import { Button } from "@/components/ui/button";
import Link from "next/link";

/**
 * Error State for Product Detail Page
 *
 * Displays when an error occurs during product data fetching
 * or rendering. Provides recovery options.
 */
export default function ProductError({
  error,
  reset,
}: {
  error: Error & { digest?: string };
  reset: () => void;
}) {
  useEffect(() => {
    // Log error to error reporting service
    console.error("Product page error:", error);
  }, [error]);

  return (
    <div className="min-h-screen flex flex-col bg-background">
      <Header />

      <main className="flex-1 flex items-center justify-center">
        <div className="container max-w-2xl mx-auto px-4 md:px-6 py-12 text-center">
          <div className="bg-card border border-border rounded-lg p-8 md:p-12">
            {/* Error Icon */}
            <div className="mx-auto w-16 h-16 rounded-full bg-destructive/10 flex items-center justify-center mb-6">
              <AlertTriangle className="h-8 w-8 text-destructive" />
            </div>

            {/* Error Message */}
            <h1 className="text-2xl md:text-3xl font-bold text-foreground mb-3">
              Произошла ошибка
            </h1>
            <p className="text-foreground-secondary mb-6 max-w-md mx-auto">
              К сожалению, не удалось загрузить страницу продукта. Пожалуйста,
              попробуйте еще раз или вернитесь на главную.
            </p>

            {/* Error Details (Development) */}
            {process.env.NODE_ENV === "development" && (
              <details className="mb-6 text-left">
                <summary className="cursor-pointer text-sm text-foreground-tertiary hover:text-foreground mb-2">
                  Технические детали (только для разработчиков)
                </summary>
                <div className="bg-destructive/5 border border-destructive/20 rounded-md p-4">
                  <p className="text-xs font-mono text-foreground-secondary break-all">
                    {error.message}
                  </p>
                  {error.digest && (
                    <p className="text-xs font-mono text-foreground-tertiary mt-2">
                      Error Digest: {error.digest}
                    </p>
                  )}
                </div>
              </details>
            )}

            {/* Action Buttons */}
            <div className="flex flex-col sm:flex-row gap-3 justify-center">
              <Button
                onClick={() => reset()}
                size="lg"
                className="gap-2"
              >
                <RefreshCw className="h-4 w-4" />
                Попробовать снова
              </Button>

              <Button
                variant="outline"
                size="lg"
                asChild
                className="gap-2"
              >
                <Link href="/">
                  <Home className="h-4 w-4" />
                  На главную
                </Link>
              </Button>
            </div>

            {/* Support Text */}
            <p className="text-sm text-foreground-tertiary mt-8">
              Если проблема сохраняется,{" "}
              <Link
                href="/support"
                className="text-primary hover:text-primary-hover underline"
              >
                свяжитесь с поддержкой
              </Link>
            </p>
          </div>
        </div>
      </main>

      <Footer />
    </div>
  );
}
